import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { LoggedInUserDto } from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';
import { cacheKeysUtil } from 'src/utils/cacheKeysUtil';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useState, useEffect, useCallback, useContext } from 'react';
import { UserDetailsResult } from './types';
import { UserContext } from './context';

/**
 * Hook to fetch user details from the API
 * This is a basic hook that returns the raw API response
 */
export const useUserDetailsQuery = (): UseQueryResult<LoggedInUserDto> =>
  useQuery(cacheKeysUtil.userDetails(), async (): Promise<LoggedInUserDto> => {
    const response = await tFinancialService.api.getLoggedInUserDetails();
    const { data } = response;
    return data?.data;
  });

/**
 * Enhanced hook for user details with additional functionality
 * This hook provides:
 * 1. Automatic fetching of user details when authenticated
 * 2. Convenient access to user properties
 * 3. Loading and error states
 * 4. Manual refetch capability
 */
export const useUserDetails = (): UserDetailsResult => {
  const [userDetails, setUserDetails] = useState<LoggedInUserDto | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);

  // Get the current path to determine if we're on the login page
  const currentPath = window.location.pathname;
  const isLoginPage = currentPath.includes('/login');

  // Only fetch user details if authenticated and not on login page
  const shouldFetchUserDetails = authStatus === 'authenticated' && !isLoginPage;

  const fetchUserDetails = useCallback(async () => {
    if (!shouldFetchUserDetails) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await tFinancialService.api.getLoggedInUserDetails();
      const userData = response.data?.data;

      if (userData) {
        setUserDetails(userData);
        // User details loaded successfully
      } else {
        throw new Error('Failed to fetch user details');
      }
    } catch (err) {
      // Avoid using console.log to prevent lint errors
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
    } finally {
      setIsLoading(false);
    }
  }, [shouldFetchUserDetails]);

  // Fetch user details when authentication status changes
  useEffect(() => {
    if (shouldFetchUserDetails) {
      fetchUserDetails();
    } else if (authStatus !== 'authenticated') {
      // Reset state when user is not authenticated
      setUserDetails(null);
      setIsLoading(false);
      setError(null);
    }
  }, [authStatus, shouldFetchUserDetails, fetchUserDetails]);

  // Provide a way to manually refetch user details
  const refetchUserDetails = async () => {
    await fetchUserDetails();
  };

  return {
    // User data
    userDetails,
    billedEntity: userDetails?.billedEntity || null,
    firstName: userDetails?.firstName || '',
    lastName: userDetails?.lastName || '',
    email: userDetails?.email || '',
    displayRole: userDetails?.displayRole || '',
    isAdmin: userDetails?.admin || false,
    isSystemAdmin: userDetails?.systemAdmin || false,

    // Status information
    isLoading,
    error,

    // Actions
    refetchUserDetails,
  };
};

/**
 * Hook to use the user context
 * This provides access to user details from the context
 */
export const useUserContext = () => useContext(UserContext);
